import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { DesktopModeNotice } from "./components/DesktopModeNotice";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { RecoverAccount } from "./pages/RecoverAccount";
import { SetupMFA } from "./pages/SetupMFA";
import { Onboarding } from "./pages/Onboarding";
import { Dashboard } from "./pages/Dashboard";
import { Organisations } from "./pages/Organisations";
import { OrganisationPage } from "./pages/OrganisationPage";
import { Suggestions } from "./pages/Suggestions";
import { Documents } from "./pages/Documents";
import { MyLife } from "./pages/MyLife";
import { Collections } from "./pages/Collections";
import { PlacementPage } from "./pages/PlacementPage";
import { SARList, SARDetail } from "./pages/SAR";
import { Profile } from "./pages/Profile";
import { SecureUpload } from "./pages/SecureUpload";
import { Donate } from "./pages/Donate";
import { DonateThankYou } from "./pages/DonateThankYou";
import { Privacy } from "./pages/Privacy";
import ForOrganisations from "./pages/ForOrganisations";
import Research from "./pages/Research";
import GuidesIndex from "./pages/guides/GuidesIndex";
import WhatIsASar from "./pages/guides/WhatIsASar";
import RequestCareRecords from "./pages/guides/RequestCareRecords";
import RequestFosteringRecords from "./pages/guides/RequestFosteringRecords";
import RequestAdoptionRecords from "./pages/guides/RequestAdoptionRecords";
import RequestSchoolRecords from "./pages/guides/RequestSchoolRecords";
import RequestMedicalRecords from "./pages/guides/RequestMedicalRecords";
import RequestPoliceRecords from "./pages/guides/RequestPoliceRecords";
import UnderstandingRedactions from "./pages/guides/UnderstandingRedactions";
import DeadlineMissed from "./pages/guides/DeadlineMissed";
import CanYouSarAFosterCarer from "./pages/guides/CanYouSarAFosterCarer";
import RecordsWrongOrUnkind from "./pages/guides/RecordsWrongOrUnkind";
import WhyTakenIntoCare from "./pages/guides/WhyTakenIntoCare";
import FoiVsSar from "./pages/guides/FoiVsSar";
import RequestChildrensHomeRecords from "./pages/guides/RequestChildrensHomeRecords";
import RequestImmigrationRecords from "./pages/guides/RequestImmigrationRecords";
import RequestCafcassCourtRecords from "./pages/guides/RequestCafcassCourtRecords";
import RecordRetentionPeriods from "./pages/guides/RecordRetentionPeriods";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrganisations from "./pages/admin/AdminOrganisations";
import AdminCandidates from "./pages/admin/AdminCandidates";
import AdminMerge from "./pages/admin/AdminMerge";
import AdminOrgDetail from "./pages/admin/AdminOrgDetail";
import AdminAnalysisOverview from "./pages/admin/AdminAnalysisOverview";
import AdminAnalysisFlagged from "./pages/admin/AdminAnalysisFlagged";
import AdminProcessDetail from "./pages/admin/AdminProcessDetail";
import AdminDocumentTypes from "./pages/admin/AdminDocumentTypes";
import AdminLibraryTags from "./pages/admin/AdminLibraryTags";
import AdminSupportOrgs from "./pages/admin/AdminSupportOrgs";
import AdminProviders from "./pages/admin/AdminProviders";
import AdminInfrastructureWorkers from "./pages/admin/AdminInfrastructureWorkers";
import AdminInfrastructureStorage from "./pages/admin/AdminInfrastructureStorage";
import AdminActivityLogs, { AdminActivityLogsUser } from "./pages/admin/AdminActivityLogs";
import AdminEarlyAccess from "./pages/admin/AdminEarlyAccess";
import AdminLeagueTable from "./pages/admin/AdminLeagueTable";
import AdminFeatureIdeas from "./pages/admin/AdminFeatureIdeas";
import AdminSarHelp from "./pages/admin/AdminSarHelp";
import AdminSarHelpDetail from "./pages/admin/AdminSarHelpDetail";
import AdminSecurity from "./pages/admin/AdminSecurity";
import Verify from "./pages/Verify";
import AccountChooser from "./pages/AccountChooser";
import { SharedAccountBanner } from "./components/SharedAccountBanner";
import { PendingRecoveryCodeDialog } from "./components/RecoveryCodeDialog";
import { ScrollToTop } from "./components/ScrollToTop";
import { UnlockGate } from "./components/UnlockGate";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects appropriately if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // For newly registered users (no MFA and no onboarding), go to MFA setup
    if (!user.mfa_enabled && !user.onboarding_complete) {
      return <Navigate to="/setup-mfa" replace />;
    }
    // For users who skipped MFA but haven't onboarded, go to onboarding
    if (!user.onboarding_complete) {
      return <Navigate to="/onboarding" replace />;
    }
    // Fully onboarded — let the account chooser decide where to land. It'll
    // auto-bounce to /dashboard if the user has no incoming shared invitations.
    return <Navigate to="/choose-account" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <Landing />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/recover" element={
        <PublicRoute>
          <RecoverAccount />
        </PublicRoute>
      } />
      
      {/* Public Upload Route (no auth required) */}
      <Route path="/upload/:token" element={<SecureUpload />} />

      {/* Public donation page — intentionally open to logged-in users too */}
      <Route path="/donate" element={<Donate />} />
      <Route path="/donate/thank-you" element={<DonateThankYou />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/for-organisations" element={<ForOrganisations />} />
      <Route path="/research" element={<Research />} />

      {/* Public guides — SEO-facing how-to content, each with CTAs into registration */}
      <Route path="/guides" element={<GuidesIndex />} />
      <Route path="/guides/what-is-a-subject-access-request" element={<WhatIsASar />} />
      <Route path="/guides/how-to-request-your-care-records-from-the-council" element={<RequestCareRecords />} />
      <Route path="/guides/how-to-request-your-fostering-records" element={<RequestFosteringRecords />} />
      <Route path="/guides/how-to-request-your-adoption-records" element={<RequestAdoptionRecords />} />
      <Route path="/guides/how-to-request-your-school-records" element={<RequestSchoolRecords />} />
      <Route path="/guides/how-to-request-your-medical-records" element={<RequestMedicalRecords />} />
      <Route path="/guides/how-to-request-your-police-records" element={<RequestPoliceRecords />} />
      <Route path="/guides/understanding-redactions-in-your-care-records" element={<UnderstandingRedactions />} />
      <Route path="/guides/how-long-do-organisations-keep-your-records" element={<RecordRetentionPeriods />} />
      <Route path="/guides/what-to-do-if-an-organisation-misses-the-deadline" element={<DeadlineMissed />} />
      <Route path="/guides/can-you-send-a-sar-to-a-foster-carer" element={<CanYouSarAFosterCarer />} />
      <Route path="/guides/how-to-request-your-childrens-home-records" element={<RequestChildrensHomeRecords />} />
      <Route path="/guides/how-to-request-your-cafcass-and-family-court-records" element={<RequestCafcassCourtRecords />} />
      <Route path="/guides/how-to-request-your-home-office-immigration-records" element={<RequestImmigrationRecords />} />
      <Route path="/guides/what-to-do-if-your-records-are-wrong-or-unkind" element={<RecordsWrongOrUnkind />} />
      <Route path="/guides/how-to-find-out-why-you-were-taken-into-care" element={<WhyTakenIntoCare />} />
      <Route path="/guides/freedom-of-information-vs-subject-access-request" element={<FoiVsSar />} />

      {/* Public verification page — anyone with the reference can check */}
      <Route path="/verify/:reference" element={<Verify />} />

      {/* Post-login account chooser (only renders if user has invitations) */}
      <Route path="/choose-account" element={
        <ProtectedRoute>
          <AccountChooser />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/setup-mfa" element={
        <ProtectedRoute>
          <SetupMFA />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/organisations" element={
        <ProtectedRoute>
          <Organisations />
        </ProtectedRoute>
      } />
      <Route path="/organisations/:id" element={
        <ProtectedRoute>
          <OrganisationPage />
        </ProtectedRoute>
      } />
      <Route path="/organisations/:orgId/placements/:placementId" element={
        <ProtectedRoute>
          <PlacementPage />
        </ProtectedRoute>
      } />
      <Route path="/suggestions" element={
        <ProtectedRoute>
          <Suggestions />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Documents />
        </ProtectedRoute>
      } />
      <Route path="/collections" element={
        <ProtectedRoute>
          <Collections />
        </ProtectedRoute>
      } />
      <Route path="/my-life" element={
        <ProtectedRoute>
          <MyLife />
        </ProtectedRoute>
      } />
      <Route path="/sar" element={
        <ProtectedRoute>
          <SARList />
        </ProtectedRoute>
      } />
      <Route path="/sar/:id" element={
        <ProtectedRoute>
          <SARDetail />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />

      {/* Admin section — gated inside AdminLayout via user.role check */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="organisations" element={<AdminOrganisations />} />
        <Route path="organisations/:orgId" element={<AdminOrgDetail />} />
        <Route path="candidates" element={<AdminCandidates />} />
        <Route path="merge" element={<AdminMerge />} />
        <Route path="analysis" element={<AdminAnalysisOverview />} />
        <Route path="analysis/process/:processId" element={<AdminProcessDetail />} />
        <Route path="analysis/prompts" element={<Navigate to="/admin/analysis/process/segmentation" replace />} />
        <Route path="analysis/ai-processes" element={<Navigate to="/admin/analysis/process/email_classification" replace />} />
        <Route path="analysis/flagged" element={<AdminAnalysisFlagged />} />
        <Route path="libraries" element={<Navigate to="/admin/libraries/document-types" replace />} />
        <Route path="libraries/document-types" element={<AdminDocumentTypes />} />
        <Route path="libraries/tags" element={<AdminLibraryTags />} />
        <Route path="libraries/support-orgs" element={<AdminSupportOrgs />} />
        <Route path="settings" element={<Navigate to="/admin/settings/providers" replace />} />
        <Route path="settings/providers" element={<AdminProviders />} />
        <Route path="infrastructure" element={<Navigate to="/admin/infrastructure/workers" replace />} />
        <Route path="infrastructure/workers" element={<AdminInfrastructureWorkers />} />
        <Route path="infrastructure/storage" element={<AdminInfrastructureStorage />} />
        <Route path="early-access" element={<AdminEarlyAccess />} />
        <Route path="league-table" element={<AdminLeagueTable />} />
        <Route path="feature-ideas" element={<AdminFeatureIdeas />} />
        <Route path="sar-help" element={<AdminSarHelp />} />
        <Route path="sar-help/:sarId" element={<AdminSarHelpDetail />} />
        <Route path="security" element={<AdminSecurity />} />
        <Route path="activity-logs" element={<AdminActivityLogs />} />
        <Route path="activity-logs/users/:userId" element={<AdminActivityLogsUser />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <ScrollToTop />
          <DesktopModeNotice />
          <SharedAccountBanner />
          <PendingRecoveryCodeDialog />
          <UnlockGate />
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
